import cn from 'classnames';
import { forwardRef } from 'react';

import { useTableRowContext } from '../table-row/table-row-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { TableCellActionProps } from './table-cell-action.types';

import './table-cell-action.scss';

/**
 * This component renders content in a table cell when a row is hovered or focused.
 * It should be used only inside `<TableRow>` component.
 * Use the property `floating` to place the content above the cell content. It is useful when space is limited.
 *
 *
 * ### Usage
 *
 * ```tsx
 * import { TableCellAction } from '@bloomreach/react-banana-ui';
 *
 * export default function RowWithAction() {
 *   return (
 *     <TableRow key={row.id}>
 *       <TableCell>Foo</TableCell>
 *       <TableCell>Bar</TableCell>
 *       <TableCell>
 *         <TableCellAction>
 *           <Button onClick={() => console.log('Edit')}>Edit</Button>
 *         </TableCellAction>
 *       </TableCell>
 *     </TableRow>
 *   );
 * }
 * ```
 *
 *
 * ### Usage (floating)
 *
 * ```tsx
 * import { TableCellAction } from '@bloomreach/react-banana-ui';
 *
 * export default function RowWithFloatingAction() {
 *   return (
 *     <TableRow key={row.id}>
 *       <TableCell>Foo</TableCell>
 *       <TableCell>Bar</TableCell>
 *       <TableCell>
 *        Loang content with floating action
 *         <TableCellAction floating>
 *           <Button onClick={() => console.log('Edit')}>Edit</Button>
 *         </TableCellAction>
 *       </TableCell>
 *     </TableRow>
 *   );
 * }
 * ```
 */
const TableCellAction = forwardRef(
  (props: TableCellActionProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): null | ReactElement => {
    const {
      children,
      className,
      floating = false,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const rowContext = useTableRowContext();

    if (!rowContext) {
      throw new Error('TableCellAction must be used within a `TableRowContext.Provider`');
    }

    const { highlighted } = rowContext;

    if (!highlighted) {
      return null;
    }

    return (
      <div
        className={cn(className, 'rbui-table-cell-action', {
          'rbui-table-cell-action--floating': floating,
        })}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

TableCellAction.displayName = 'TableCellAction';

export default TableCellAction;
